import { REPO_ACTION_MATRIX } from "../config/repoActionMatrix";
import {
  isDownloadableRepoItem,
  isChatEligibleRepoItem,
} from "./repoActionUtils";

/**
 * Returns all actions that should be visible for a repo item.
 *
 * This is the ONLY place that interprets the Action Matrix.
 *
 * Dynamic rules applied here:
 * - Rename only for single selection (floating actions)
 * - Folder download only when folder contains downloadable files
 * - Restore only for deleted personal files
 * - Train File only for saved files in My Drive
 */

export function getAvailableActions({
  item,
  role,
  repoView,
  ui = "menu", // menu | floating
  selectionCount = 1,
  items = [],
}) {
  if (!item) return [];

  //----------------------------------------------------------
  // Determine Matrix Role
  //----------------------------------------------------------

  const matrixRole = role === "admin" ? "admin" : "user";

  //----------------------------------------------------------
  // Determine Repository
  //----------------------------------------------------------

  let repository;

  switch (repoView) {
    case "personal":
      repository = "personal";
      break;

    case "global":
      repository = "global";
      break;

    case "group":
      repository = "group";
      break;

    case "personal_space":
      repository = "myDrive";
      break;

    default:
      return [];
  }

  //----------------------------------------------------------
  // Determine Item Type
  //----------------------------------------------------------

  const isFolder = item.type === "folder";

  //----------------------------------------------------------
  // Determine Status
  //----------------------------------------------------------

  let status;

  if (repository === "myDrive") {
    if (isFolder) {
      status = "folder";
    } else {
      status = String(item.fileStatus) === "0"
        ? "deleted"
        : "saved";
    }
  } else {
    if (isFolder) {
      status = "folder";
    } else {
      status = String(item.approvalStatus || "").toLowerCase();
    }
  }

  //----------------------------------------------------------
  // Lookup Matrix
  //----------------------------------------------------------

  const matrix =
    REPO_ACTION_MATRIX?.[matrixRole]?.[repository]?.[status];

  if (!matrix) {
    return [];
  }

  let actions = [...(matrix[ui] || [])];

  //----------------------------------------------------------
  // Dynamic Rules
  //----------------------------------------------------------

  //
  // Rename only for single selection in Floating Actions
  //

  if (ui === "floating" && selectionCount !== 1) {
    actions = actions.filter((a) => a !== "rename");
  }

  //
  // Folder Download
  //

  if (isFolder && actions.includes("download")) {
    const hasDownloadableChildren = items.some(
      (child) =>
        String(child.parentId) === String(item.id) &&
        isDownloadableRepoItem(child)
    );

    if (!hasDownloadableChildren) {
      actions = actions.filter((a) => a !== "download");
    }
  }

  //
  // Chat
  //

  if (actions.includes("chat")) {
    if (!isChatEligibleRepoItem(item)) {
      actions = actions.filter((a) => a !== "chat");
    }
  }

  //
  // Restore
  //

  if (actions.includes("restoreFile")) {
    if (!(item.isPersonalSpace && String(item.fileStatus) === "0")) {
      actions = actions.filter((a) => a !== "restoreFile");
    }
  }

  //
  // Train File
  //

  if (actions.includes("trainFile")) {
    if (
      !item.isPersonalSpace ||
      String(item.fileStatus) !== "1"
    ) {
      actions = actions.filter((a) => a !== "trainFile");
    }
  }

  return actions;
}